import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { Client } from '../../../services/clientService';
import { currencyService } from '../../../services/currencyService';
import { getInvoicePdfUrl } from '../../../services/adminPaymentLinkService';
import type { Service } from '../../../services/servicesStore';
import type {
  AdminOrder,
  AdminOrderLineItem,
  OrderDeliveryMode,
  OrderFulfillmentStatus,
  OrderPaymentStatus,
  OrderSource,
} from '../../../services/adminOrderService';

interface OrderDetailsModalProps {
  order: AdminOrder | null;
  clients: Client[];
  services: Service[];
  defaultMode?: 'view' | 'create';
  onClose: () => void;
  onSave: (order: AdminOrder) => void;
}

const PAYMENT_STATUS_OPTIONS: Array<{
  value: OrderPaymentStatus;
  label: string;
}> = [
  { value: 'draft', label: 'Brouillon' },
  { value: 'awaiting_payment', label: 'Paiement en attente' },
  { value: 'partially_paid', label: 'Paiement partiel' },
  { value: 'paid', label: 'Paye' },
  { value: 'refunded', label: 'Rembourse' },
  { value: 'canceled', label: 'Annule' },
];

const FULFILLMENT_STATUS_OPTIONS: Array<{
  value: OrderFulfillmentStatus;
  label: string;
}> = [
  { value: 'new', label: 'Nouveau' },
  { value: 'scheduled', label: 'Planifie' },
  { value: 'in_progress', label: 'En production' },
  { value: 'delivered', label: 'Livre' },
  { value: 'completed', label: 'Termine' },
  { value: 'on_hold', label: 'En pause' },
  { value: 'canceled', label: 'Annule' },
];

const SOURCE_OPTIONS: Array<{ value: OrderSource; label: string }> = [
  { value: 'manual', label: 'Manuel' },
  { value: 'payment_link', label: 'Lien de paiement' },
  { value: 'checkout', label: 'Checkout' },
];

const DELIVERY_MODE_OPTIONS: Array<{ value: OrderDeliveryMode; label: string }> = [
  { value: 'service', label: 'Prestation' },
  { value: 'project', label: 'Projet' },
  { value: 'digital', label: 'Livraison digitale' },
  { value: 'subscription', label: 'Abonnement' },
];

const PAYMENT_STATUS_BADGES: Record<OrderPaymentStatus, string> = {
  draft: 'bg-slate-100 text-slate-700',
  awaiting_payment: 'bg-amber-100 text-amber-700',
  partially_paid: 'bg-orange-100 text-orange-700',
  paid: 'bg-emerald-100 text-emerald-700',
  refunded: 'bg-sky-100 text-sky-700',
  canceled: 'bg-gray-100 text-gray-600',
};

const FULFILLMENT_STATUS_BADGES: Record<OrderFulfillmentStatus, string> = {
  new: 'bg-slate-100 text-slate-700',
  scheduled: 'bg-indigo-100 text-indigo-700',
  in_progress: 'bg-blue-100 text-blue-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-green-100 text-green-700',
  on_hold: 'bg-amber-100 text-amber-700',
  canceled: 'bg-gray-100 text-gray-600',
};

const formatDateInput = (value?: string) => {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toISOString().slice(0, 10);
};

const formatDateTime = (value?: string) => {
  if (!value) {
    return 'Non renseigne';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Non renseigne';
  }

  return parsed.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const resolveCurrencyCode = (value?: string) => value?.trim().toUpperCase() || 'EUR';

const roundAmount = (value: number) => {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Number(value.toFixed(2));
};

const generateLineItemId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `item_${crypto.randomUUID()}`;
  }

  return `item_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
};

const normalizeLineItem = (
  lineItem: Partial<AdminOrderLineItem>,
  fallbackCurrencyCode: string
): AdminOrderLineItem => {
  const quantityRaw = Number(lineItem.quantity ?? 1);
  const quantity =
    Number.isFinite(quantityRaw) && Math.trunc(quantityRaw) > 0
      ? Math.trunc(quantityRaw)
      : 1;
  const unitAmountRaw = Number(lineItem.unitAmount ?? 0);
  const unitAmount =
    Number.isFinite(unitAmountRaw) && unitAmountRaw > 0 ? roundAmount(unitAmountRaw) : 0;
  const label = String(lineItem.label ?? '');

  return {
    id: lineItem.id || generateLineItemId(),
    serviceId: lineItem.serviceId?.trim() || undefined,
    label,
    quantity,
    unitAmount,
    lineTotal: roundAmount(unitAmount * quantity),
    currencyCode: resolveCurrencyCode(lineItem.currencyCode || fallbackCurrencyCode),
  };
};

const computeOrderAmountFromLineItems = (lineItems: AdminOrderLineItem[]) =>
  roundAmount(lineItems.reduce((total, lineItem) => total + lineItem.lineTotal, 0));

const ensureOrderLineItems = (order: AdminOrder): AdminOrderLineItem[] => {
  const currencyCode = resolveCurrencyCode(order.currencyCode);

  if (Array.isArray(order.lineItems) && order.lineItems.length > 0) {
    return order.lineItems.map((lineItem) => normalizeLineItem(lineItem, currencyCode));
  }

  if (order.serviceName.trim() || Number(order.amount || 0) > 0) {
    return [
      normalizeLineItem(
        {
          serviceId: order.serviceId || undefined,
          label: order.serviceName || '',
          quantity: 1,
          unitAmount: Number(order.amount || 0),
        },
        currencyCode
      ),
    ];
  }

  return [normalizeLineItem({}, currencyCode)];
};

const applyLineItemsToOrder = (
  order: AdminOrder,
  rawLineItems: AdminOrderLineItem[]
): AdminOrder => {
  const currencyCode = resolveCurrencyCode(order.currencyCode);
  const lineItems = rawLineItems.map((lineItem) => normalizeLineItem(lineItem, currencyCode));
  const amount = computeOrderAmountFromLineItems(lineItems);
  const primaryLineItem = lineItems.find((lineItem) => lineItem.label.trim().length > 0);

  return {
    ...order,
    lineItems,
    currencyCode,
    amount,
    serviceName: primaryLineItem?.label.trim() || order.serviceName,
    serviceId: primaryLineItem?.serviceId || order.serviceId,
  };
};

const prepareOrderForEdit = (order: AdminOrder): AdminOrder =>
  applyLineItemsToOrder(order, ensureOrderLineItems(order));

const prepareOrderForSave = (order: AdminOrder): AdminOrder => {
  const currencyCode = resolveCurrencyCode(order.currencyCode);
  const normalizedLineItems = ensureOrderLineItems({ ...order, currencyCode });
  const filteredLineItems = normalizedLineItems.filter(
    (lineItem) => lineItem.label.trim().length > 0 && lineItem.lineTotal > 0
  );

  if (filteredLineItems.length > 0) {
    return applyLineItemsToOrder({ ...order, currencyCode }, filteredLineItems);
  }

  if (Number(order.amount || 0) > 0) {
    const fallbackItem = normalizeLineItem(
      {
        serviceId: order.serviceId || undefined,
        label: order.serviceName || 'Service',
        quantity: 1,
        unitAmount: Number(order.amount || 0),
      },
      currencyCode
    );

    return applyLineItemsToOrder({ ...order, currencyCode }, [fallbackItem]);
  }

  return {
    ...order,
    currencyCode,
    lineItems: [],
    amount: 0,
  };
};

const OrderDetailsModal = ({
  order,
  clients,
  services,
  defaultMode = 'view',
  onClose,
  onSave,
}: OrderDetailsModalProps) => {
  const [editMode, setEditMode] = useState(defaultMode === 'create');
  const [formData, setFormData] = useState<AdminOrder | null>(
    order ? prepareOrderForEdit(order) : order
  );
  const [displayCountry, setDisplayCountry] = useState(currencyService.getCountry());
  const submitRequestedRef = useRef(false);

  useEffect(() => {
    setFormData(order ? prepareOrderForEdit(order) : order);
    setEditMode(defaultMode === 'create');
    submitRequestedRef.current = false;
  }, [order, defaultMode]);

  useEffect(() => {
    return currencyService.subscribe((nextCountry) => {
      setDisplayCountry(nextCountry);
    });
  }, []);

  useEffect(() => {
    if (!order) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [order, onClose]);

  if (!order || !formData) {
    return null;
  }

  const setField = <K extends keyof AdminOrder>(field: K, value: AdminOrder[K]) => {
    setFormData((current) => (current ? { ...current, [field]: value } : current));
  };

  const displayAmountLabel = currencyService.formatAmountForDisplay(
    formData.amount,
    formData.currencyCode,
    {
      country: displayCountry,
      display: 'code',
    }
  );
  const originalAmountLabel = currencyService.formatCurrencyAmount(
    formData.amount,
    formData.currencyCode,
    { display: 'code' }
  );
  const visibleLineItems = formData.lineItems.filter(
    (lineItem) => lineItem.label.trim().length > 0 || lineItem.lineTotal > 0
  );

  const handleClientChange = (clientId: string) => {
    const client = clients.find((entry) => entry.id === clientId);
    if (!client) {
      return;
    }

    setFormData((current) => {
      if (!current) {
        return current;
      }

      const nextCurrencyCode = resolveCurrencyCode(
        current.currencyCode || client.currencyCode || 'EUR'
      );
      const nextOrder: AdminOrder = {
        ...current,
        clientId: client.id,
        clientName: client.name,
        clientEmail: client.email,
        clientPhone: client.phone || '',
        clientCompany: client.company || '',
        currencyCode: nextCurrencyCode,
      };

      const hasConfiguredLineItems = current.lineItems.some(
        (lineItem) => lineItem.label.trim().length > 0 || lineItem.unitAmount > 0
      );
      if (hasConfiguredLineItems) {
        return nextOrder;
      }

      if ((client.serviceName || '').trim() || Number(client.totalAmount || 0) > 0) {
        const seededLineItem = normalizeLineItem(
          {
            serviceId: client.serviceId || undefined,
            label: client.serviceName || '',
            quantity: 1,
            unitAmount: Number(client.totalAmount || 0),
          },
          nextCurrencyCode
        );

        return applyLineItemsToOrder(
          {
            ...nextOrder,
            serviceId: client.serviceId || nextOrder.serviceId,
            serviceName: client.serviceName || nextOrder.serviceName,
          },
          [seededLineItem]
        );
      }

      return nextOrder;
    });
  };

  const handleCurrencyChange = (nextCurrencyCode: string) => {
    const currencyCode = resolveCurrencyCode(nextCurrencyCode);
    setFormData((current) => {
      if (!current) {
        return current;
      }

      return applyLineItemsToOrder(
        {
          ...current,
          currencyCode,
        },
        current.lineItems.map((lineItem) => ({
          ...lineItem,
          currencyCode,
        }))
      );
    });
  };

  const handleAddLineItem = () => {
    setFormData((current) => {
      if (!current) {
        return current;
      }

      return applyLineItemsToOrder(current, [
        ...current.lineItems,
        normalizeLineItem({}, current.currencyCode),
      ]);
    });
  };

  const handleRemoveLineItem = (lineItemId: string) => {
    setFormData((current) => {
      if (!current) {
        return current;
      }

      const nextLineItems = current.lineItems.filter((lineItem) => lineItem.id !== lineItemId);
      if (nextLineItems.length === 0) {
        nextLineItems.push(normalizeLineItem({}, current.currencyCode));
      }

      return applyLineItemsToOrder(current, nextLineItems);
    });
  };

  const handleLineItemChange = (
    lineItemId: string,
    field: 'label' | 'quantity' | 'unitAmount',
    value: string
  ) => {
    setFormData((current) => {
      if (!current) {
        return current;
      }

      return applyLineItemsToOrder(
        current,
        current.lineItems.map((lineItem) => {
          if (lineItem.id !== lineItemId) {
            return lineItem;
          }

          if (field === 'label') {
            return {
              ...lineItem,
              label: value,
            };
          }

          if (field === 'quantity') {
            const quantityRaw = Number(value || 1);
            const quantity =
              Number.isFinite(quantityRaw) && Math.trunc(quantityRaw) > 0
                ? Math.trunc(quantityRaw)
                : 1;

            return {
              ...lineItem,
              quantity,
            };
          }

          const unitAmountRaw = Number(value || 0);
          const unitAmount =
            Number.isFinite(unitAmountRaw) && unitAmountRaw > 0 ? unitAmountRaw : 0;

          return {
            ...lineItem,
            unitAmount,
          };
        })
      );
    });
  };

  const handleLineItemServiceChange = (lineItemId: string, serviceId: string) => {
    setFormData((current) => {
      if (!current) {
        return current;
      }

      if (!serviceId) {
        return applyLineItemsToOrder(
          current,
          current.lineItems.map((lineItem) =>
            lineItem.id === lineItemId
              ? {
                  ...lineItem,
                  serviceId: undefined,
                }
              : lineItem
          )
        );
      }

      const service = services.find((entry) => entry.id === serviceId);
      if (!service) {
        return current;
      }

      const convertedUnitAmount =
        service.price > 0
          ? currencyService.convertAmount(
              service.price,
              'EUR',
              resolveCurrencyCode(current.currencyCode)
            )
          : 0;

      return applyLineItemsToOrder(
        current,
        current.lineItems.map((lineItem) =>
          lineItem.id === lineItemId
            ? {
                ...lineItem,
                serviceId: service.id,
                label: service.name,
                unitAmount: lineItem.unitAmount > 0 ? lineItem.unitAmount : convertedUnitAmount,
              }
            : lineItem
        )
      );
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!submitRequestedRef.current) {
      return;
    }
    submitRequestedRef.current = false;

    onSave(prepareOrderForSave(formData));
    setEditMode(false);
  };

  const armSubmitIntent = () => {
    submitRequestedRef.current = true;
    window.setTimeout(() => {
      submitRequestedRef.current = false;
    }, 0);
  };

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4">
            <div>
              <h2 className="text-xl font-semibold">
                {editMode ? 'Modifier la commande' : 'Fiche commande'}
              </h2>
              <p className="mt-1 text-sm text-gray-500">{formData.orderNumber}</p>
            </div>
            <button
              type="button"
              onClick={() => {
                submitRequestedRef.current = false;
                onClose();
              }}
              aria-label="Fermer la fiche commande"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-2xl leading-none text-gray-700 transition-colors hover:bg-gray-200"
            >
              ×
            </button>
          </div>

          <div className="overflow-y-auto p-6">
            {editMode ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Numero de commande
                    </label>
                    <input
                      type="text"
                      value={formData.orderNumber}
                      onChange={(event) => setField('orderNumber', event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Source
                    </label>
                    <select
                      value={formData.source}
                      onChange={(event) =>
                        setField('source', event.target.value as OrderSource)
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    >
                      {SOURCE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Type de livraison
                    </label>
                    <select
                      value={formData.deliveryMode}
                      onChange={(event) =>
                        setField('deliveryMode', event.target.value as OrderDeliveryMode)
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    >
                      {DELIVERY_MODE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <div className="mb-4 text-sm font-semibold text-gray-700">Client</div>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Fiche client
                      </label>
                      <select
                        value={formData.clientId}
                        onChange={(event) => handleClientChange(event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                      >
                        <option value="">Selectionner un client</option>
                        {clients.map((client) => (
                          <option key={client.id} value={client.id}>
                            {client.name} - {client.email}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Nom
                      </label>
                      <input
                        type="text"
                        value={formData.clientName}
                        onChange={(event) => setField('clientName', event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.clientEmail}
                        onChange={(event) => setField('clientEmail', event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Telephone
                      </label>
                      <input
                        type="text"
                        value={formData.clientPhone}
                        onChange={(event) => setField('clientPhone', event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Entreprise
                      </label>
                      <input
                        type="text"
                        value={formData.clientCompany}
                        onChange={(event) => setField('clientCompany', event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold text-gray-700">Lignes facture</div>
                    <button
                      type="button"
                      onClick={handleAddLineItem}
                      className="rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100"
                    >
                      + Ajouter service
                    </button>
                  </div>
                  <div className="mb-4 grid gap-3 md:grid-cols-[140px_minmax(0,1fr)]">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Devise
                      </label>
                      <select
                        value={formData.currencyCode}
                        onChange={(event) => handleCurrencyChange(event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                      >
                        {currencyService.getSupportedCurrencyCodes().map((currencyCode) => (
                          <option key={currencyCode} value={currencyCode}>
                            {currencyCode}
                          </option>
                        ))}
                      </select>
                    </div>
                    <p className="self-end pb-2 text-xs text-gray-500">
                      Ajoute plusieurs lignes pour une facture complete (service principal +
                      upsells/options).
                    </p>
                  </div>

                  <div className="space-y-3">
                    {formData.lineItems.map((lineItem, index) => {
                      const lineTotalLabel = currencyService.formatCurrencyAmount(
                        lineItem.lineTotal,
                        formData.currencyCode,
                        { display: 'code' }
                      );

                      return (
                        <div
                          key={lineItem.id}
                          className="rounded-xl border border-gray-200 bg-white p-4"
                        >
                          <div className="mb-3 flex items-center justify-between gap-3">
                            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                              Ligne {index + 1}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveLineItem(lineItem.id)}
                              disabled={formData.lineItems.length <= 1}
                              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                                formData.lineItems.length <= 1
                                  ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                                  : 'bg-red-50 text-red-600 hover:bg-red-100'
                              }`}
                            >
                              Supprimer
                            </button>
                          </div>

                          <div className="grid gap-3 lg:grid-cols-[220px_minmax(0,1fr)_90px_140px_auto]">
                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-600">
                                Service catalogue
                              </label>
                              <select
                                value={lineItem.serviceId || ''}
                                onChange={(event) =>
                                  handleLineItemServiceChange(lineItem.id, event.target.value)
                                }
                                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                              >
                                <option value="">Personnalise</option>
                                {services.map((service) => (
                                  <option key={service.id} value={service.id}>
                                    {service.name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-600">
                                Libelle
                              </label>
                              <input
                                type="text"
                                value={lineItem.label}
                                onChange={(event) =>
                                  handleLineItemChange(lineItem.id, 'label', event.target.value)
                                }
                                placeholder="Ex: Setup tracking"
                                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                              />
                            </div>

                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-600">
                                Qte
                              </label>
                              <input
                                type="number"
                                min="1"
                                step="1"
                                value={lineItem.quantity}
                                onChange={(event) =>
                                  handleLineItemChange(
                                    lineItem.id,
                                    'quantity',
                                    event.target.value
                                  )
                                }
                                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                              />
                            </div>

                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-600">
                                Prix unitaire
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={lineItem.unitAmount}
                                onChange={(event) =>
                                  handleLineItemChange(
                                    lineItem.id,
                                    'unitAmount',
                                    event.target.value
                                  )
                                }
                                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                              />
                            </div>

                            <div className="flex items-end">
                              <div>
                                <div className="mb-1 text-xs font-medium text-gray-600">
                                  Total ligne
                                </div>
                                <div className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-900">
                                  {lineTotalLabel}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="mt-3 text-xs text-gray-500">
                    Affichage admin actuel: {displayAmountLabel}. Montant d origine:
                    {' '}{originalAmountLabel}.
                  </p>
                </div>

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Statut paiement
                    </label>
                    <select
                      value={formData.paymentStatus}
                      onChange={(event) =>
                        setField(
                          'paymentStatus',
                          event.target.value as OrderPaymentStatus
                        )
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    >
                      {PAYMENT_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Statut livraison
                    </label>
                    <select
                      value={formData.fulfillmentStatus}
                      onChange={(event) =>
                        setField(
                          'fulfillmentStatus',
                          event.target.value as OrderFulfillmentStatus
                        )
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    >
                      {FULFILLMENT_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Date commande
                    </label>
                    <input
                      type="date"
                      value={formatDateInput(formData.orderedAt)}
                      onChange={(event) =>
                        setField(
                          'orderedAt',
                          event.target.value
                            ? new Date(`${event.target.value}T12:00:00`).toISOString()
                            : new Date().toISOString()
                        )
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Date de livraison
                    </label>
                    <input
                      type="date"
                      value={formatDateInput(formData.deliveryDate)}
                      onChange={(event) =>
                        setField(
                          'deliveryDate',
                          event.target.value
                            ? new Date(`${event.target.value}T12:00:00`).toISOString()
                            : ''
                        )
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Date paiement
                    </label>
                    <input
                      type="date"
                      value={formatDateInput(formData.paidAt)}
                      onChange={(event) =>
                        setField(
                          'paidAt',
                          event.target.value
                            ? new Date(`${event.target.value}T12:00:00`).toISOString()
                            : ''
                        )
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Tracking / ref livraison
                    </label>
                    <input
                      type="text"
                      value={formData.trackingReference}
                      onChange={(event) =>
                        setField('trackingReference', event.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Numero de devis
                    </label>
                    <input
                      type="text"
                      value={formData.quoteNumber}
                      onChange={(event) => setField('quoteNumber', event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Cle lien paiement
                    </label>
                    <input
                      type="text"
                      value={formData.paymentLinkKey}
                      onChange={(event) =>
                        setField('paymentLinkKey', event.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Notes client
                    </label>
                    <textarea
                      rows={5}
                      value={formData.notes}
                      onChange={(event) => setField('notes', event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Notes internes
                    </label>
                    <textarea
                      rows={5}
                      value={formData.internalNotes}
                      onChange={(event) =>
                        setField('internalNotes', event.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 border-t pt-6">
                  {defaultMode !== 'create' && (
                    <button
                      type="button"
                      onClick={() => {
                        submitRequestedRef.current = false;
                        setEditMode(false);
                      }}
                      className="rounded-lg bg-gray-100 px-5 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-200"
                    >
                      Annuler
                    </button>
                  )}
                  <button
                    type="submit"
                    onClick={armSubmitIntent}
                    className="rounded-lg bg-neon-orange px-5 py-3 font-medium text-white transition-colors hover:bg-[#FF4500]"
                  >
                    Enregistrer la commande
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <div className="text-sm text-gray-500">Paiement</div>
                    <span
                      className={`mt-3 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        PAYMENT_STATUS_BADGES[formData.paymentStatus]
                      }`}
                    >
                      {
                        PAYMENT_STATUS_OPTIONS.find(
                          (option) => option.value === formData.paymentStatus
                        )?.label
                      }
                    </span>
                  </div>
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <div className="text-sm text-gray-500">Livraison</div>
                    <span
                      className={`mt-3 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        FULFILLMENT_STATUS_BADGES[formData.fulfillmentStatus]
                      }`}
                    >
                      {
                        FULFILLMENT_STATUS_OPTIONS.find(
                          (option) => option.value === formData.fulfillmentStatus
                        )?.label
                      }
                    </span>
                  </div>
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <div className="text-sm text-gray-500">Montant</div>
                    <div className="mt-3 text-2xl font-bold text-gray-900">
                      {displayAmountLabel}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      Base enregistree: {originalAmountLabel}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <div className="text-sm text-gray-500">Derniere mise a jour</div>
                    <div className="mt-3 text-sm font-medium text-gray-900">
                      {formatDateTime(formData.lastUpdateAt)}
                    </div>
                  </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                  <section className="rounded-2xl border border-gray-200 p-5">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Client & commande
                    </h3>
                    <div className="mt-4 space-y-3 text-sm text-gray-700">
                      <div>
                        <strong>Client :</strong> {formData.clientName}
                      </div>
                      <div>
                        <strong>Email :</strong> {formData.clientEmail}
                      </div>
                      <div>
                        <strong>Entreprise :</strong>{' '}
                        {formData.clientCompany || 'Non renseignee'}
                      </div>
                      <div>
                        <strong>Telephone :</strong>{' '}
                        {formData.clientPhone || 'Non renseigne'}
                      </div>
                      <div>
                        <strong>Service principal :</strong>{' '}
                        {formData.serviceName || visibleLineItems[0]?.label || 'Non renseigne'}
                      </div>
                      <div>
                        <strong>Lignes facture :</strong> {visibleLineItems.length}
                      </div>
                      <div>
                        <strong>Source :</strong>{' '}
                        {
                          SOURCE_OPTIONS.find((option) => option.value === formData.source)
                            ?.label
                        }
                      </div>
                      <div>
                        <strong>Type :</strong>{' '}
                        {
                          DELIVERY_MODE_OPTIONS.find(
                            (option) => option.value === formData.deliveryMode
                          )?.label
                        }
                      </div>
                    </div>
                  </section>

                  <section className="rounded-2xl border border-gray-200 p-5">
                    <h3 className="text-lg font-semibold text-gray-900">Execution</h3>
                    <div className="mt-4 space-y-3 text-sm text-gray-700">
                      <div>
                        <strong>Date de commande :</strong> {formatDateTime(formData.orderedAt)}
                      </div>
                      <div>
                        <strong>Date de paiement :</strong> {formatDateTime(formData.paidAt)}
                      </div>
                      <div>
                        <strong>Date de livraison :</strong>{' '}
                        {formatDateTime(formData.deliveryDate)}
                      </div>
                      <div>
                        <strong>Tracking / ref :</strong>{' '}
                        {formData.trackingReference || 'Non renseigne'}
                      </div>
                      <div>
                        <strong>Numero de devis :</strong>{' '}
                        {formData.quoteNumber || 'Non relie'}
                      </div>
                      <div>
                        <strong>Cle lien paiement :</strong>{' '}
                        {formData.paymentLinkKey || 'Non relie'}
                      </div>
                      {formData.paymentLinkKey && (
                        <div className="flex flex-wrap gap-2 pt-1">
                          <a
                            href={`/devis/${encodeURIComponent(formData.paymentLinkKey)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            Ouvrir devis client
                          </a>
                          <a
                            href={getInvoicePdfUrl(formData.paymentLinkKey)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            Télécharger facture PDF
                          </a>
                        </div>
                      )}
                    </div>
                  </section>
                </div>

                <section className="rounded-2xl border border-gray-200 p-5">
                  <h3 className="text-lg font-semibold text-gray-900">Lignes facture</h3>
                  {visibleLineItems.length === 0 ? (
                    <p className="mt-4 text-sm text-gray-600">
                      Aucune ligne facture pour le moment.
                    </p>
                  ) : (
                    <div className="mt-4 space-y-3">
                      {visibleLineItems.map((lineItem) => (
                        <div
                          key={lineItem.id}
                          className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 md:flex-row md:items-center md:justify-between"
                        >
                          <div>
                            <div className="font-medium text-gray-900">
                              {lineItem.label || 'Service'}
                            </div>
                            <div className="text-xs text-gray-500">
                              Qte {lineItem.quantity} x{' '}
                              {currencyService.formatCurrencyAmount(
                                lineItem.unitAmount,
                                formData.currencyCode,
                                { display: 'code' }
                              )}
                            </div>
                          </div>
                          <div className="text-base font-semibold text-gray-900">
                            {currencyService.formatCurrencyAmount(
                              lineItem.lineTotal,
                              formData.currencyCode,
                              { display: 'code' }
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 flex items-center justify-end gap-3 border-t border-gray-200 pt-4 text-sm">
                    <span className="text-gray-500">Total facture</span>
                    <span className="text-lg font-semibold text-neon-orange">
                      {originalAmountLabel}
                    </span>
                  </div>
                </section>

                <div className="grid gap-6 lg:grid-cols-2">
                  <section className="rounded-2xl border border-gray-200 p-5">
                    <h3 className="text-lg font-semibold text-gray-900">Notes client</h3>
                    <p className="mt-4 whitespace-pre-line text-sm leading-6 text-gray-700">
                      {formData.notes || 'Aucune note client pour le moment.'}
                    </p>
                  </section>
                  <section className="rounded-2xl border border-gray-200 p-5">
                    <h3 className="text-lg font-semibold text-gray-900">Notes internes</h3>
                    <p className="mt-4 whitespace-pre-line text-sm leading-6 text-gray-700">
                      {formData.internalNotes || 'Aucune note interne pour le moment.'}
                    </p>
                  </section>
                </div>

                <div className="flex justify-end gap-3 border-t pt-6">
                  <button
                    type="button"
                    onClick={() => {
                      submitRequestedRef.current = false;
                      setEditMode(true);
                    }}
                    className="rounded-lg bg-gray-100 px-5 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-200"
                  >
                    Modifier
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      submitRequestedRef.current = false;
                      onClose();
                    }}
                    className="rounded-lg bg-neon-orange px-5 py-3 font-medium text-white transition-colors hover:bg-[#FF4500]"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default OrderDetailsModal;
